import { useDeferredValue, useMemo } from 'react';
import { useActiveClip } from '~/features/clips/selectors';
import { useColDividers, useRowDividers } from '~/features/grid/selectors';
import { useStore } from '~/store';
import type { Frame } from '~/types/pipeline';
import { allBlobIndices, allCells, buildFrames, buildFramesFromBlobs } from './pipeline';

/**
 * Build sprite frames from the committed matte snapshot. Inputs are passed
 * through `useDeferredValue` so heavy per-cell trim doesn't run on every drag
 * tick.
 *
 * Picks frames from the active clip's source. If there is no active clip, falls
 * back to the full grid (uniform mode) or all blobs in order (pin mode).
 * If the active clip's source kind doesn't match the current framing mode,
 * returns an empty Frame[].
 */
export function useFrames(options: { includeMatches?: boolean } = {}): Frame[] {
  const includeMatches = options.includeMatches ?? true;
  const cleaned = useStore((s) => s.workflow.committedCleaned);
  const cropRect = useDeferredValue(useStore((s) => s.grid.cropRect));
  const cols = useDeferredValue(useColDividers());
  const rows = useDeferredValue(useRowDividers());
  const pivot = useStore((s) => s.frames.pivot);
  const framingMode = useStore((s) => s.pin.framingMode);
  const blobs = useStore((s) => s.pin.blobs);
  const overrides = useStore((s) => s.pin.overrides);
  const order = useStore((s) => s.pin.order);
  const matches = useStore((s) => s.referencePoint.results);
  const activeClip = useActiveClip();
  const alignmentMode = activeClip?.alignmentMode ?? 'bottom-center';
  const source = activeClip?.source;
  const activeMatches = includeMatches && alignmentMode === 'reference-point' ? matches : null;

  return useMemo(() => {
    if (!cleaned || !cropRect) return [];
    if (framingMode === 'pin') {
      if (!blobs) return [];
      // Active clip must be a blob clip in pin mode; otherwise fall back to all blobs.
      const blobIndices =
        source?.kind === 'blobs'
          ? source.blobs
          : source?.kind === 'cells'
            ? []
            : allBlobIndices(blobs, order);
      if (blobIndices.length === 0) return [];
      return buildFramesFromBlobs({
        cleaned,
        cropRect,
        blobs,
        overrides,
        alignmentMode,
        blobIndices,
        ...(activeMatches ? { matches: activeMatches } : {}),
      });
    }
    if (cols.length < 2 || rows.length < 2) return [];
    const rowCount = rows.length - 1;
    const colCount = cols.length - 1;
    // Active clip must be a cell clip in uniform mode; otherwise fall back to full grid.
    const cells =
      source?.kind === 'cells'
        ? source.cells
        : source?.kind === 'blobs'
          ? []
          : allCells(rowCount, colCount);
    if (cells.length === 0) return [];
    return buildFrames({
      cleaned,
      cropRect,
      rows,
      cols,
      alignmentMode,
      pivot,
      cells,
      ...(activeMatches ? { matches: activeMatches } : {}),
    });
  }, [
    cleaned,
    cropRect,
    rows,
    cols,
    alignmentMode,
    pivot,
    framingMode,
    blobs,
    overrides,
    order,
    activeMatches,
    source,
  ]);
}
